import { Validator } from 'fluentvalidation-ts';
import { fieldRequiredValidationMessage } from '../../../validators/sharedValidators';
import { BuildTypeCode } from '../../projects/project';
import { QuoteResponse } from '../../quotes/quoteResponse';
import { DataForQuoteOrDesignForm } from '../../shared/quote-or-design-form/GetDataForQuoteOrDesignForm';
import { ThermostatTypeCode } from '../../shared/quote-or-design-form/quoteOrDesignFormData';
import {
  UnitFormModel,
  UnitFormModelValidator,
  unitHasHeatPump,
  unitHasManifoldsIfItHasHeatPump,
  unitHasSolar,
  unitHasUnderfloorHeating,
  unitOnlyHasManifoldsWithRooms,
} from '../../shared/quote-or-design-form/units/unitFormModel';
import {
  haveAtLeastOneUnit,
  haveAtMostOneRadiatorManifoldOnEachUnit,
  haveHeatPumpOnEveryUnitThatHasRadiatorManifold,
} from '../../shared/quote-or-design-form/units/unitValidators';
import { mapUnitResponseToFormModel } from '../../shared/units/unitResponse';
import { every, some } from 'lodash';

export type DesignFormModel = {
  designId: number | null;
  title?: string;
  revisionCode?: string;
  revisionReason?: string;
  qrNumber?: string;
  address?: string;
  underfloorHeatingSalesOrderId: number | null;
  heatPumpSalesOrderId: number | null;
  solarSalesOrderId: number | null;
  isDraft: boolean;
  defaultThermostatTypeCode: ThermostatTypeCode;
  includeNeoHub: boolean;
  units: Array<UnitFormModel>;
  focusedUnitId: string | null;
  // This is for validation errors that relate to the units tree as a whole rather than to individual fields
  unitsValidation: null;
};

export const getInitialDesignFormModel = (
  quoteResponse: QuoteResponse,
  referenceData: DataForQuoteOrDesignForm,
  projectBuildType: BuildTypeCode,
): DesignFormModel => ({
  designId: null,
  title: quoteResponse.name,
  underfloorHeatingSalesOrderId: null,
  heatPumpSalesOrderId: null,
  solarSalesOrderId: null,
  isDraft: true,
  focusedUnitId: null,
  defaultThermostatTypeCode: quoteResponse.thermostatTypeCode,
  includeNeoHub: quoteResponse.includeNeoHub,
  units: quoteResponse.units.map(unitResponse =>
    mapUnitResponseToFormModel({
      unitResponse,
      referenceData,
      projectBuildType,
      mapRoomlessManifoldDetailsToRoom: true,
      createDesignDetails: true,
      overrideHasPipelineMapToTrue: true,
    }),
  ),
  unitsValidation: null,
});

export class DesignFormModelValidator extends Validator<DesignFormModel> {
  constructor() {
    super();

    this.ruleFor('title')
      .maxLength(100)
      .withMessage('Please enter no more than 100 characters');

    this.ruleFor('revisionReason')
      .maxLength(100)
      .withMessage('Please enter no more than 100 characters');

    this.ruleFor('qrNumber')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .maxLength(50)
      .withMessage('Please enter no more than 50 characters');

    this.ruleFor('address')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .maxLength(1000)
      .withMessage('Please enter no more than 1000 characters');

    this.ruleForEach('units').setValidator(design => new UnitFormModelValidator(design.isDraft, 'Design'));

    this.ruleFor('unitsValidation')
      .must(haveAtLeastOneUnit)
      .must(haveHeatPumpOnEveryUnitThatHasRadiatorManifold)
      .must(haveAtMostOneRadiatorManifoldOnEachUnit);

    this.ruleFor('unitsValidation')
      .must((_, { units }) => every(units, unit => unitOnlyHasManifoldsWithRooms(unit)))
      .withMessage('All Manifolds should have Rooms (details cannot be entered at Manifold level).')
      .must((_, { units }) => every(units, unit => unitHasManifoldsIfItHasHeatPump(unit)))
      .withMessage('Each Unit with a Heat Pump must also have some Rooms.');
  }
}

export const designFormModelValidator = new DesignFormModelValidator();

export const designHasUnderfloorHeating = (design: DesignFormModel) => some(design.units, unitHasUnderfloorHeating);

export const designHasHeatPump = (design: DesignFormModel) => some(design.units, unitHasHeatPump);

export const designHasSolar = (design: DesignFormModel) => some(design.units, unitHasSolar);
